// Alpine components shared by the email composer:
//   composeAttachments — drag/drop + chip-list backed by a real <input type=file>
//   composeToolbar     — signature swap + saved-reply template insertion

const SIGNATURE_MARKER = "data-signature";
const FORWARD_HEADER = /^-{5,}/;

function findEditorRoot(el) {
  return el.closest("form")?.querySelector("[x-data^='tiptapEditor']") || null;
}

function readEditorHtml(editorRoot) {
  return editorRoot?.querySelector("input[name='body_html']")?.value || "";
}

function buildSignatureNode(doc, signature) {
  const node = doc.createElement("div");
  node.setAttribute(SIGNATURE_MARKER, "true");
  node.setAttribute("data-signature-id", signature.id);
  node.innerHTML = signature.body_html || "";
  return node;
}

function hasContent(node) {
  return (
    Boolean(node.textContent.trim()) ||
    node.nodeName === "IMG" ||
    Boolean(node.querySelector?.("img, [data-proposal-tag]"))
  );
}

// Where the earlier mail begins on a reply or forward. The editor drops the gmail_quote
// wrapper, so after an edit a reply is known by the last thing in it being a blockquote,
// with its "On ..., ... wrote:" line above it.
function quotedChainStart(wrapper) {
  const written = Array.from(wrapper.children).filter(hasContent);
  const marked = written.find((el) => el.matches(".gmail_quote") || FORWARD_HEADER.test(el.textContent.trim()));
  if (marked) return marked;

  const last = written[written.length - 1];
  if (last?.nodeName !== "BLOCKQUOTE") return null;

  const attribution = last.previousElementSibling;
  return attribution?.nodeName === "P" && /:\s*$/.test(attribution.textContent) ? attribution : last;
}

// Below what the user wrote and above any earlier mail. With nothing written yet it goes
// after the empty first paragraph, which leaves that paragraph to write in.
function insertSignature(wrapper, node) {
  const nodes = Array.from(wrapper.childNodes);
  const chain = quotedChainStart(wrapper);
  const writing = chain ? nodes.slice(0, nodes.indexOf(chain)) : nodes;
  const lastWritten = writing.filter(hasContent).pop();
  const firstChild = wrapper.firstChild;

  if (lastWritten) {
    lastWritten.after(node);
  } else if (firstChild?.nodeName === "P" && !hasContent(firstChild)) {
    firstChild.after(node);
  } else {
    wrapper.insertBefore(node, firstChild);
  }
}

// Replace the signature block in place, preserving what surrounds it, or add one.
function applySignature(html, signature) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html || "";

  const existing = wrapper.querySelector(`[${SIGNATURE_MARKER}]`);
  if (existing) {
    if (signature) {
      existing.replaceWith(buildSignatureNode(document, signature));
    } else {
      existing.remove();
    }
    return wrapper.innerHTML;
  }

  if (!signature) return wrapper.innerHTML;

  insertSignature(wrapper, buildSignatureNode(document, signature));
  return wrapper.innerHTML;
}

function formatBytes(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// A visible tab reveals x-show on an animation frame, and $nextTick releases on a timeout
// that can run first, so a hidden element takes no focus and measures nothing. Wait for a
// frame queued after Alpine's. A hidden tab reveals on a timeout, and has no frames to wait for.
export function onceShown(component, callback) {
  component.$nextTick(() => {
    if (document.visibilityState === "visible") {
      requestAnimationFrame(callback);
    } else {
      callback();
    }
  });
}

export function focusOnceShown(component, element) {
  onceShown(component, () => element.focus());
}

export function registerComposeHelpers(Alpine) {
  Alpine.data("composeAttachments", function ({ maxBytes, maxTotalBytes }) {
    return {
      files: [],
      dragging: false,
      error: null,
      _dragDepth: 0,

      onFileInputChange(event) {
        const list = Array.from(event.target.files || []);
        this.addFiles(list);
        // Reset native input so dataTransfer below is the single source of truth.
        event.target.value = "";
        this._syncInput();
      },

      onDragEnter() {
        this._dragDepth += 1;
        this.dragging = true;
      },
      onDragOver() {
        this.dragging = true;
      },
      onDragLeave() {
        this._dragDepth = Math.max(0, this._dragDepth - 1);
        if (this._dragDepth === 0) this.dragging = false;
      },
      onDrop(event) {
        this.dragging = false;
        this._dragDepth = 0;
        const dropped = Array.from(event.dataTransfer?.files || []);
        if (dropped.length === 0) return;
        this.addFiles(dropped);
        this._syncInput();
      },

      addFiles(incoming) {
        this.error = null;
        for (const file of incoming) {
          if (file.size > maxBytes) {
            this.error =
              window.I18n?.t?.("email_messages.compose.attach_too_large", {
                name: file.name,
                max: formatBytes(maxBytes)
              }) || `${file.name} is larger than ${formatBytes(maxBytes)}.`;
            continue;
          }
          const total = this.totalBytes() + file.size;
          if (total > maxTotalBytes) {
            this.error =
              window.I18n?.t?.("email_messages.compose.attach_total_too_large", { max: formatBytes(maxTotalBytes) }) ||
              `Total attachments exceed ${formatBytes(maxTotalBytes)}.`;
            break;
          }
          file.__cid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          this.files.push(file);
        }
      },

      removeAt(idx) {
        this.files.splice(idx, 1);
        this.error = null;
        this._syncInput();
        this._focusAfterRemoval(idx);
      },

      // The pressed button is gone with its row, and focus would fall out of the dialog.
      _focusAfterRemoval(idx) {
        const input = this.$refs.fileInput;
        this.$nextTick(() => {
          const buttons = input.closest("[data-attachments]").querySelectorAll("[data-attachment-remove]");
          (buttons[idx] ?? buttons[buttons.length - 1] ?? input).focus();
        });
      },

      totalBytes() {
        return this.files.reduce((sum, f) => sum + (f.size || 0), 0);
      },

      formatSize(bytes) {
        return formatBytes(bytes);
      },

      _syncInput() {
        const input = this.$refs.fileInput;
        if (!input) return;
        const dt = new DataTransfer();
        this.files.forEach((f) => dt.items.add(f));
        input.files = dt.files;
      }
    };
  });

  Alpine.data("composeToolbar", function (signatures, templates, defaultId) {
    return {
      signatures,
      templates,
      selectedSignatureId: defaultId || "",
      templatesOpen: false,

      openTemplates() {
        this.templatesOpen = true;
        focusOnceShown(this, this.$refs.templatesClose);
      },

      closeTemplates() {
        this.templatesOpen = false;
        this.$refs.templatesTrigger?.focus();
      },

      keepTabInTemplates(event) {
        const buttons = Array.from(this.$refs.templatesPanel.querySelectorAll("button"));
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        const edge = event.shiftKey ? first : last;
        const active = document.activeElement;
        if (active === edge || active === this.$refs.templatesPanel) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      },

      swapSignature() {
        const editorRoot = findEditorRoot(this.$el);
        if (!editorRoot) return;
        const signature = this.signatures.find((s) => s.id === this.selectedSignatureId) || null;
        const newHtml = applySignature(readEditorHtml(editorRoot), signature);
        editorRoot.dispatchEvent(new CustomEvent("tiptap:set-content", { detail: { content: newHtml } }));
      },

      insertTemplate(reply) {
        if (!reply) return;
        const editorRoot = findEditorRoot(this.$el);
        if (!editorRoot) return;
        editorRoot.dispatchEvent(
          new CustomEvent("tiptap:insert-content", { detail: { content: reply.body_html || "" } })
        );

        const subjectInput = this.$el.closest("form")?.querySelector("input[name='subject']");
        if (subjectInput && !subjectInput.value && reply.subject) {
          subjectInput.value = reply.subject;
        }
      }
    };
  });
}

export default registerComposeHelpers;
